import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Image, Tooltip } from 'antd';
import { useMachineDetailsRobotSchedule } from '../../../../context/MachineDetailsRobotScheduleContext';
import type { SelectedScheduleRouteRoutine } from '../../../../types/MachineDetailsRobotSchedule.types';
import { getRoutineThumbnail } from '../../../../utils/routeRoutine';
import { parseIso8601DurationMinutes } from '../../../../utils/schedule';
import {
  CleaningScheduleCardContainer,
  CleaningScheduleCardContent,
  CleaningScheduleCardDescription,
  CleaningScheduleCardThumbnailEmpty,
  CleaningScheduleCardTitle,
} from './CleaningScheduleCard.styles';
import { PendingSpinnerIcon } from 'lib/components/PendingSpinnerIcon/PendingSpinnerIcon';
import { selectRobotScheduleRoutes } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotSchedule/robotScheduleSelectors';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';

interface CleaningScheduleCardB50Props {
  selectedRouteRoutine: SelectedScheduleRouteRoutine;
  onEdit: () => void;
}

export const CleaningScheduleCardB50 = ({
  selectedRouteRoutine,
  onEdit,
}: CleaningScheduleCardB50Props): JSX.Element => {
  const { t } = useTranslation();
  const { isReadOnly } = useMachineDetailsRobotSchedule();

  const isRoutine = selectedRouteRoutine.type === 'routine';
  const selectedInstance = isRoutine ? selectedRouteRoutine.routine : selectedRouteRoutine.route;
  const routes = useSelector(selectRobotScheduleRoutes) ?? [];
  const thumbnail = isRoutine
    ? getRoutineThumbnail({ routine: selectedRouteRoutine.routine, routes })
    : selectedRouteRoutine.route.thumbnail;
  // Presigned URLs can expire while the panel stays open; treat a failed load as unavailable.
  const [failedThumbnail, setFailedThumbnail] = useState<string>();

  return (
    <CleaningScheduleCardContainer>
      <Flex align="center" gap={kccThemeConstants.SPACING.SM}>
        {thumbnail && thumbnail !== failedThumbnail ? (
          <Image
            width={48}
            height={48}
            src={thumbnail}
            style={{ objectFit: 'cover' }}
            alt=""
            preview={false}
            onError={(): void => setFailedThumbnail(thumbnail)}
            placeholder={
              <Flex align="center" justify="center" style={{ width: '100%', height: '100%' }}>
                <PendingSpinnerIcon size={16} />
              </Flex>
            }
          />
        ) : (
          <Tooltip title={t('liveMap.mapUnavailable')}>
            <CleaningScheduleCardThumbnailEmpty role="img" tabIndex={0} aria-label={t('liveMap.mapUnavailable')}>
              <SvgIcon name="image" size={24} />
            </CleaningScheduleCardThumbnailEmpty>
          </Tooltip>
        )}

        <CleaningScheduleCardContent vertical gap={2}>
          <Flex align="center" justify="space-between">
            <CleaningScheduleCardTitle>{selectedInstance.name}</CleaningScheduleCardTitle>

            {!isReadOnly && (
              <Button
                type="link"
                aria-label={t('common.edit')}
                icon={<SvgIcon name="editNew" />}
                onClick={onEdit}
                style={{ width: kccThemeConstants.SIZE.S250, height: kccThemeConstants.SIZE.S250 }}
              />
            )}
          </Flex>

          <CleaningScheduleCardDescription>
            {t('createSchedule.durationInMinutes', {
              duration: parseIso8601DurationMinutes(selectedInstance.duration),
            })}
          </CleaningScheduleCardDescription>
        </CleaningScheduleCardContent>
      </Flex>
    </CleaningScheduleCardContainer>
  );
};
